import { DateField, Input } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import PersonalIdInput from '@/components/PersonalIdInput';
import { CY_STAKEHOLDER_CONTAINER_IDS } from '@/constants/stakeholders';
import * as personalIdUtils from '@/utils/personalId';

const {
  INPUT_FIRST_NAME,
  INPUT_LAST_NAME,
  INPUT_PERSONAL_ID,
  INPUT_DATE_OF_BIRTH,
} = CY_STAKEHOLDER_CONTAINER_IDS;

/**
 * Get all base form field objects for a given stakeholder, deployment region and merchant country.
 * @param {object} stakeholder Object containing stakeholder data
 * @param {string} deploymentRegion
 * @param {string} merchantCountry
 * @returns Object containing all form field objects for the given stakeholder.
 */
export const getFields = (
  stakeholder = {},
  deploymentRegion,
  merchantCountry,
  merchantEntityType,
) => {
  const {
    dateOfBirth,
    firstName,
    hasPersonalId,
    isPersonalIdPristine,
    lastName,
    personalId = '',
    address: { country, street, city, postalCode, region } = {},
  } = stakeholder;

  const { translationKey, mask, prefix } = personalIdUtils.getField(
    deploymentRegion,
    merchantCountry,
    country.value,
    merchantEntityType,
  );

  return {
    firstName: {
      ...firstName,
      component: Input,
      type: 'text',
      name: 'first_name',
      extraProps: {
        centerOnFocus: 'start',
        ...generateCyElemId(INPUT_FIRST_NAME, window),
      },
    },
    lastName: {
      ...lastName,
      component: Input,
      type: 'text',
      name: 'last_name',
      extraProps: {
        ...generateCyElemId(INPUT_LAST_NAME, window),
      },
    },
    country: {
      ...country,
      name: 'country',
      stakeholderKey: 'address.country',
    },
    postalCode: {
      ...postalCode,
      name: 'postal_code',
      stakeholderKey: 'address.postalCode',
    },
    street: {
      ...street,
      name: 'address',
      stakeholderKey: 'address.street',
    },
    city: {
      ...city,
      name: 'city',
      stakeholderKey: 'address.city',
    },
    region: {
      ...region,
      name: country.value === 'US' ? 'state' : 'region',
      stakeholderKey: 'address.region',
    },
    ...(translationKey
      ? {
          personalId: {
            ...personalId,
            component: PersonalIdInput,
            size: 1,
            type: 'text',
            isOptional: false,
            name: `personal_id.${translationKey}`,
            extraProps: {
              ...generateCyElemId(INPUT_PERSONAL_ID, window),
              hasPersonalId, // This is the persisted
              isPersonalIdPristine: isPersonalIdPristine.value,
              mask,
              prefix,
            },
          },
        }
      : {}),
    dateOfBirth: {
      ...dateOfBirth,
      component: DateField,
      size: 1,
      type: 'date',
      name: 'dateOfBirth',
      extraProps: {
        ...generateCyElemId(INPUT_DATE_OF_BIRTH, window),
        monthAsLeftmost: merchantCountry === 'US',
      },
    },
  };
};
